
import React from "react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CheckCircle, Info, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

export interface BrandAlertProps {
  type?: "info" | "success" | "warning" | "error";
  title?: string;
  className?: string;
  children: React.ReactNode;
}

/**
 * Alert component that follows the VirtuStaff brand guidelines
 */
export const BrandAlert: React.FC<BrandAlertProps> = ({
  type = "info",
  title,
  className,
  children,
}) => {
  const getAlertStyles = () => {
    switch (type) {
      case "success":
        return {
          variant: "default",
          className: "bg-green-50 text-green-800 border-green-200",
          icon: <CheckCircle className="h-4 w-4 text-green" />
        };
      case "warning":
        return {
          variant: "default",
          className: "bg-yellow-50 text-yellow-800 border-yellow-200",
          icon: <AlertTriangle className="h-4 w-4 text-yellow" />
        };
      case "error":
        return {
          variant: "destructive",
          className: "bg-red-50 text-red-800 border-red-200",
          icon: <AlertCircle className="h-4 w-4 text-red" />
        };
      case "info":
      default:
        return {
          variant: "default",
          className: "bg-blue-50 text-blue-800 border-blue-200",
          icon: <Info className="h-4 w-4 text-info" />
        };
    }
  };

  const { variant, className: alertClassName, icon } = getAlertStyles();

  return (
    <Alert variant={variant as any} className={cn(alertClassName, className)}>
      {icon}
      {title && <AlertTitle>{title}</AlertTitle>}
      <AlertDescription>{children}</AlertDescription>
    </Alert>
  );
};

export default BrandAlert;
